import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import { logo, logoWhite } from '../../assets';
import { usefulLinks } from '../../constants/footer';
import { getGlobalData } from '../../api/strapi/utils';
import { useLanguage } from '../../context';
import { getLocalizedPath } from '../../helpers/routes';
import {
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Mail,
  Smartphone,
  MapPin,
  Clock,
  ChevronDown
} from 'lucide-react';

const Footer = () => {
  const d = new Date();
  const location = useLocation();
  const intl = useIntl();
  const [strapiData, setStrapiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchStrapiData = async () => {
      const result = await getGlobalData();
      if (!result.ok) {
        if (result.reason !== "NOT_FOUND") {
          console.warn("Footer global data unavailable:", result);
        }
        setStrapiData(null);
        setLoading(false);
        return;
      }

      setStrapiData(result.data?.attributes || result.data);
      setLoading(false);
    };

    fetchStrapiData();
  }, []);

  // Use only Strapi data
  const currentData = strapiData;
  const logoUrl = strapiData && strapiData.logoWhite 
    ? `${process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}${strapiData.logoWhite.url}`
    : logoWhite;

  return (
    <footer className="bg-[#0a1628] text-white">
      {/* Top Section with Logo and Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <NavLink to={"/"}>
                <img
                  className="block w-auto h-16"
                  src={logoUrl}
                  alt="Logo"
                />
              </NavLink>
            </div>

            {/* Newsletter Form */}
            <div className="w-full md:w-80">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder={intl.formatMessage({ id: "footer.newsletter.placeholder" })}
                  className="flex-grow px-4 py-2.5 bg-white/10 border border-white/15 text-white placeholder-slate-400 focus:outline-none focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A] transition-colors"
                />
                <button className="bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors">
                  {intl.formatMessage({ id: "footer.newsletter.subscribe" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Useful Links - Using Strapi Data */}
          <div>
            <h3 className="relative text-xs font-semibold uppercase tracking-[0.2em] text-white mb-6 pb-3 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-8 after:bg-[#16A34A]">
              <FormattedMessage
                id="useful.links"
                defaultMessage="Useful links"
              />
            </h3>
            <ul className="space-y-2">
              <>
                <li><NavLink to={getLocalizedPath("/info/company/company", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors duration-300 ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="company.profile" defaultMessage="Company Profile" /></NavLink></li>
                <li><NavLink to={getLocalizedPath("/contact", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors duration-300 ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="contact.us" defaultMessage="Contact Us" /></NavLink></li>
                <li><NavLink to={getLocalizedPath("/careers", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors duration-300 ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="careers" defaultMessage="Careers" /></NavLink></li>
                <li><NavLink to={getLocalizedPath("/faq", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors duration-300 ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="faq" defaultMessage="FAQ" /></NavLink></li>
              </>
            </ul>
          </div>

          {/* About Section - Using Strapi Data */}
          <div>
            <h3 className="relative text-xs font-semibold uppercase tracking-[0.2em] text-white mb-6 pb-3 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-8 after:bg-[#16A34A]">
              <FormattedMessage id="about.us" defaultMessage="About Us" />
            </h3>
            <ul className="space-y-2">
              <li><NavLink to={getLocalizedPath("/info/company/company", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="company.profile" defaultMessage="Company Profile" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/info/company/partenariat-africa-systems-and-adroit-infosystems", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="partnership" defaultMessage="Partenariat" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/info/r-and-d", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="research.development" defaultMessage="Research & Development" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/info/industries", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="industries" defaultMessage="Industries" /></NavLink></li>
            </ul>
          </div>

          {/* Products & Services - Using Strapi Data */}
          <div>
            <h3 className="relative text-xs font-semibold uppercase tracking-[0.2em] text-white mb-6 pb-3 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-8 after:bg-[#16A34A]">
              <FormattedMessage id="products.services" defaultMessage="Products & Services" />
            </h3>
            <ul className="space-y-2">
              <li><NavLink to={getLocalizedPath("/info/produits", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="products" defaultMessage="Products" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/info/services", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="services" defaultMessage="Services" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/info/company/company", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="solutions" defaultMessage="Solutions" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/info/industries", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="industry.solutions" defaultMessage="Industry Solutions" /></NavLink></li>
            </ul>
          </div>

          {/* Support & Resources - Using Strapi Data */}
          <div>
            <h3 className="relative text-xs font-semibold uppercase tracking-[0.2em] text-white mb-6 pb-3 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-8 after:bg-[#16A34A]">
              <FormattedMessage id="support.resources" defaultMessage="Support & Resources" />
            </h3>
            <ul className="space-y-2">
              <li><NavLink to={getLocalizedPath("/faq", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="faq" defaultMessage="FAQ" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/documentation", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="documentation" defaultMessage="Documentation" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/training", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="training" defaultMessage="Training" /></NavLink></li>
              <li><NavLink to={getLocalizedPath("/contact", language)} className={({ isActive }) => `text-slate-400 hover:text-[#16A34A] transition-colors ${isActive ? "text-white font-semibold" : ""}`}><FormattedMessage id="support" defaultMessage="Support" /></NavLink></li>
            </ul>
          </div>

          {/* Social Media & App Download */}
          <div>
            <h3 className="relative text-xs font-semibold uppercase tracking-[0.2em] text-white mb-6 pb-3 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-8 after:bg-[#16A34A]">
              <FormattedMessage id="connect.with.us" defaultMessage="Connect With Us" />
            </h3>

            {/* Social Media */}
            <div className="mb-4">
              <p className="text-slate-400 mb-2"><FormattedMessage id="footer.followUsLabel" defaultMessage="Follow Us:" /></p>
              <div className="flex space-x-4">
                {currentData?.socials ? (
                  currentData.socials.map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-slate-400 hover:text-white hover:bg-[#16A34A] hover:border-[#16A34A] transition-all duration-300"
                    >
                      <span className="text-xl">{getSocialIcon(social.icon)}</span>
                    </a>
                  ))
                ) : (
                  <div>{intl.formatMessage({ id: "empty.section" })}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="border-t border-white/10 py-8">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Address */}
            <div className="flex items-start">
              <MapPin className="flex-shrink-0 h-5 w-5 text-[#16A34A] mt-1 mr-3" />
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-1">{intl.formatMessage({ id: "contact.section.addressLabel" })}</h4>
                <p className="text-sm text-slate-400">{currentData?.adress || intl.formatMessage({ id: "contact.section.addressFallback" })}</p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start">
              <Mail className="flex-shrink-0 h-5 w-5 text-[#16A34A] mt-1 mr-3" />
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-1">{intl.formatMessage({ id: "contact.section.emailLabel" })}</h4>
                <p className="text-sm text-slate-400">{currentData?.email || intl.formatMessage({ id: "contact.section.emailFallback" })}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start">
              <Smartphone className="flex-shrink-0 h-5 w-5 text-[#16A34A] mt-1 mr-3" />
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-1">{intl.formatMessage({ id: "contact.section.phoneLabel" })}</h4>
                <p className="text-sm text-slate-400">{currentData?.phone || intl.formatMessage({ id: "contact.section.phoneFallback" })}</p>
              </div>
            </div>

            {/* Business Hours */}
            <div className="flex items-start">
              <Clock className="flex-shrink-0 h-5 w-5 text-[#16A34A] mt-1 mr-3" />
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-1">{intl.formatMessage({ id: "contact.section.hoursLabel" })}</h4>
                <p className="text-sm text-slate-400">{currentData?.hour || intl.formatMessage({ id: "contact.section.hoursFallback" })}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright and Legal Links */}
      <div className="bg-black/20 border-t border-white/10 py-6">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-slate-400 text-sm mb-4 md:mb-0">
              &copy; {d.getFullYear()} {currentData?.copyright || 'Your Company Name'} - {currentData?.slogan}
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <>
                <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#16A34A] transition-colors">
                  <FormattedMessage id="legal.notice" defaultMessage="Legal Notice" />
                </a>
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#16A34A] transition-colors">
                  <FormattedMessage id="terms.of.use" defaultMessage="Terms of Use" />
                </a>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#16A34A] transition-colors">
                  <FormattedMessage id="privacy.policy" defaultMessage="Privacy Policy" />
                </a>
              </>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Helper function to get social media icons based on Strapi data
const getSocialIcon = (iconName) => {
  const iconSize = 20;
  switch(iconName.toLowerCase()) {
    case 'twitter':
      return <Twitter size={iconSize} />;
    case 'youtube':
      return <Youtube size={iconSize} />;
    case 'facebook':
      return <Facebook size={iconSize} />;
    case 'instagram':
      return <Instagram size={iconSize} />;
    case 'linkedin':
      return <Linkedin size={iconSize} />;
    default:
      return <span>{iconName.charAt(0).toUpperCase()}</span>;
  }
};

export default Footer;

